import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { sendContactForm } from '../../util/api.js';
import { parse } from '../../util/urlHelpers.js';
import { countryOptions } from '../../util/countryOptions.js';
import {
    LayoutSingleColumn,
    PrimaryButton,
    Form,
    FieldSelect,
    FieldTextInput,
    FieldPhoneNumberInput,
    FieldCheckboxGroup,
    FieldToggleGroup,
    NamedLink,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import StaticPage from '../PageBuilder/StaticPage.js';
import Swal from 'sweetalert2';
import css from './ContactPage.module.css';

const ContactPageComponent = (props) => {
    const { intl, currentUser, location } = props;
    const [loading, setLoading] = useState(false);

    const config = useConfiguration();
    const search = parse(location.search);
    const { variant } = search;
    const isInquiryVariant = variant == "inquiry";
    const isCreateListingVariant = variant == "createListing";
    const isApplyVariant = variant == "apply";

    const isDifferentVariant = isInquiryVariant || isCreateListingVariant || isApplyVariant;
    const initialValues = isDifferentVariant ? { contactReason: "membershipApplication" } : {};

    const categories = config.categoryConfiguration.categories || [];
    const isLoggedIn = !!currentUser;

    const pageMetaProps = {
        "title": intl.formatMessage({ id: 'CP.meta.title' }),
        "description": intl.formatMessage({ id: 'General.meta.text' }),
        "schema": {
            "@context": "http://schema.org",
            "@type": "Article",
            "description": intl.formatMessage({ id: 'General.meta.text' }),
            "name": intl.formatMessage({ id: 'CP.meta.title' }),
            "headline": intl.formatMessage({ id: 'CP.meta.title' }),
        },
        "socialSharing": {
            "title": intl.formatMessage({ id: 'CP.meta.title' }),
            "description": intl.formatMessage({ id: 'General.meta.text' }),
            "images1200": null,
            "images600": null
        },
    };

    const onSubmit = values => {
        setLoading(true);
        const payload = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            countryCode: values.countryCode,
            phoneNumber: values.phoneNumber,
            linkedinUrl: values.linkedinUrl,
            company: values.company,
            jobTitle: values.jobTitle,
            country: values.country,
            principalType: values.principalType,
            assetClassInterest: values.assetClassInterest,
            contactReason: values.contactReason,
            extraInfo: values.extraInfo,
        };
        sendContactForm(payload)
            .then(() => {
                Swal.fire({
                    title: intl.formatMessage({ id: 'CP.alert.success.title' }),
                    text: intl.formatMessage({ id: 'CP.alert.success.text' }),
                    icon: 'success',
                    showCloseButton: true,
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            })
            .catch(() => {
                Swal.fire({
                    title: intl.formatMessage({ id: 'CP.alert.fail.title' }),
                    text: intl.formatMessage({ id: 'CP.alert.fail.text' }),
                    icon: 'error',
                    showCloseButton: true,
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return <StaticPage className={css.root} {...pageMetaProps}>
        <LayoutSingleColumn
            topbar={<TopbarContainer currentPage="ContactPage" />}
            footer={<FooterContainer />}
        >
            <div as="main" className={css.main}>
                <h1 className={css.title}>
                    <FormattedMessage id={isDifferentVariant ? "CP.titleInquiry" : "CP.title"} />

                    {isLoggedIn ? null : <NamedLink
                        className={css.loginButton}
                        name="LoginPage"
                    >
                        <FormattedMessage id="CP.loginButton" />
                    </NamedLink>}
                </h1>

                <p className={css.text}>
                    <FormattedMessage id={isDifferentVariant ? "CP.textInquiry" : "CP.text"} />
                </p>

                <FinalForm
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    mutators={{ ...arrayMutators }}
                    render={fieldRenderProps => {
                        const {
                            handleSubmit,
                            invalid,
                        } = fieldRenderProps;

                        const submitDisabled = invalid || loading;

                        return <Form className={css.form} onSubmit={handleSubmit}>
                            <div className={css.nameRow}>
                                <FieldTextInput
                                    className={css.firstNameRoot}
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    autoComplete="given-name"
                                    label="First name"
                                    placeholder="First name"
                                    validate={validators.required("First name is required")}
                                />

                                <FieldTextInput
                                    className={css.lastNameRoot}
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    autoComplete="family-name"
                                    label="Last name"
                                    placeholder="Last name"
                                    validate={validators.required("Last name is required")}
                                />
                            </div>

                            <FieldTextInput
                                className={css.field}
                                type="email"
                                id="email"
                                name="email"
                                label={intl.formatMessage({ id: 'CP.emailLabel' })}
                                placeholder={intl.formatMessage({ id: 'CP.emailPlaceholder' })}
                                validate={validators.required(
                                    intl.formatMessage({ id: 'CP.emailRequired' })
                                )}
                            />

                            <FieldPhoneNumberInput
                                className={css.field}
                                id="phoneNumber"
                                name="phoneNumber"
                                label="Phone number"
                                placeholder={intl.formatMessage({ id: 'CP.phoneNumberPlaceholder' })}
                                countryCodeName="countryCode"
                            />

                            <FieldTextInput
                                className={css.field}
                                type="text"
                                id="linkedinUrl"
                                name="linkedinUrl"
                                label="LinkedIn URL"
                                placeholder="https://www.linkedin.com/in/..."
                            />

                            <FieldTextInput
                                className={css.field}
                                type="text"
                                id="company"
                                name="company"
                                label="Company"
                                placeholder="Company"
                            />

                            <FieldTextInput
                                className={css.field}
                                type="text"
                                id="jobTitle"
                                name="jobTitle"
                                label="Job title"
                                placeholder="Job title"
                                validate={validators.required("Job title is required")}
                            />

                            <FieldSelect
                                id="country"
                                name="country"
                                className={css.field}
                                label="Country"
                                validate={validators.required("Country is required")}
                            >
                                <option disabled value="">
                                    Select your country
                                </option>
                                {[...countryOptions]
                                    .sort((a, b) => a.label.localeCompare(b.label))
                                    .map(c => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                            </FieldSelect>

                            <FieldSelect
                                id="principalType"
                                name="principalType"
                                className={css.field}
                                label="Principal type"
                                validate={validators.required("Principal type is required")}
                            >
                                <option disabled value="">
                                    Select your principal type
                                </option>
                                <option value="buySide">Buy-side</option>
                                <option value="sellSide">Sell-side</option>
                                <option value="both">Both</option>
                                <option value="advisorOrRepresentative">Advisor or Representative</option>
                            </FieldSelect>

                            <FieldToggleGroup
                                className={css.field}
                                id="assetClassInterest"
                                name="assetClassInterest"
                                label="Asset class interest"
                                options={categories.map(c => ({
                                    key: c.id,
                                    label: c.name,
                                }))}
                            />

                            <FieldSelect
                                id="contactReason"
                                name="contactReason"
                                className={css.field}
                                label={intl.formatMessage({ id: 'CP.contactReasonLabel' })}
                                validate={validators.required(
                                    intl.formatMessage({ id: 'CP.contactReasonRequired' })
                                )}
                            >
                                <option disabled value="">
                                    {intl.formatMessage({ id: 'CP.contactReasonPlaceholder' })}
                                </option>
                                <option value="membershipApplication">
                                    {intl.formatMessage({ id: 'CP.contactReason.membershipApplication' })}
                                </option>
                                <option value="assetToSell">
                                    {intl.formatMessage({ id: 'CP.contactReason.assetToSell' })}
                                </option>
                                <option value="searchingForAsset">
                                    {intl.formatMessage({ id: 'CP.contactReason.searchingForAsset' })}
                                </option>
                                <option value="debtRequirement">
                                    {intl.formatMessage({ id: 'CP.contactReason.debtRequirement' })}
                                </option>
                                <option value="iAmInterestedInAListingOnThePlatform">
                                    {intl.formatMessage({ id: 'CP.contactReason.iAmInterestedInAListingOnThePlatform' })}
                                </option>
                            </FieldSelect>

                            <FieldTextInput
                                className={css.field}
                                type="textarea"
                                id="extraInfo"
                                name="extraInfo"
                                label="Message"
                                placeholder="Write your message here..."
                            />

                            <FieldCheckboxGroup
                                className={css.field}
                                id="terms"
                                name="terms"
                                options={[{
                                    key: 'tos-and-privacy',
                                    label: 'I agree to the Terms of Service and Privacy Policy',
                                }]}
                                validate={validators.requiredFieldArrayCheckbox(
                                    "You must agree to the Terms and Conditions"
                                )}
                            />

                            <PrimaryButton
                                className={css.submitButton}
                                type="submit"
                                inProgress={loading}
                                disabled={submitDisabled}
                            >
                                <FormattedMessage id="CP.submitButton" />
                            </PrimaryButton>
                        </Form>
                    }}
                />
            </div>
        </LayoutSingleColumn>
    </StaticPage>
};

const mapStateToProps = state => {
    const { currentUser } = state.user;

    return {
        currentUser,
    };
};

const ContactPage = compose(
    connect(mapStateToProps),
    injectIntl,
)(ContactPageComponent);

export default ContactPage;
